// Identity React Query hooks

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  getCurrentUser,
  getUserById,
  updateUserRole,
  getAvailableRolesFromApi,
  getTenantMembers,
  removeTenantMember,
  createInvitation,
  acceptInvitation,
  getTenantInvitations,
  revokeInvitation,
  resendInvitation,
} from '../services';
import type { RoleInfo } from '../services';
import type {
  User,
  Invitation,
  TenantMember,
  CreateInvitationRequest,
  UpdateUserRoleRequest,
  RemoveMemberRequest,
} from '../types';

// Query keys
export const identityKeys = {
  all: ['identity'] as const,
  users: () => [...identityKeys.all, 'users'] as const,
  user: (id: string) => [...identityKeys.users(), id] as const,
  currentUser: () => [...identityKeys.users(), 'me'] as const,
  members: (tenantId: string) =>
    [...identityKeys.all, 'members', tenantId] as const,
  invitations: (tenantId: string) =>
    [...identityKeys.all, 'invitations', tenantId] as const,
};

// =============================================================================
// USER HOOKS
// =============================================================================

/**
 * Hook to get the current authenticated user
 */
export function useCurrentUser() {
  return useQuery({
    queryKey: identityKeys.currentUser(),
    queryFn: async () => {
      const result = await getCurrentUser();
      if (!result.isSuccess) {
        throw new Error(result.message || 'Failed to get current user');
      }
      return result.value as User;
    },
  });
}

/**
 * Hook to get a user by ID
 */
export function useUser(userId: string) {
  return useQuery({
    queryKey: identityKeys.user(userId),
    queryFn: async () => {
      const result = await getUserById(userId);
      if (!result.isSuccess) {
        throw new Error(result.message || 'Failed to get user');
      }
      return result.value as User;
    },
    enabled: !!userId,
  });
}

// =============================================================================
// ROLE HOOKS
// =============================================================================

/**
 * Hook to get available roles from the API
 */
export function useAvailableRoles(tenantId: string) {
  return useQuery({
    queryKey: [...identityKeys.all, 'roles', tenantId] as const,
    queryFn: async () => {
      const result = await getAvailableRolesFromApi(tenantId);
      if (!result.isSuccess) {
        throw new Error(result.message || 'Failed to get available roles');
      }
      return result.value as RoleInfo[];
    },
    enabled: !!tenantId,
  });
}

/**
 * Hook to update a user's role
 */
export function useUpdateUserRole() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (request: UpdateUserRoleRequest) => {
      const result = await updateUserRole(request);
      if (!result.isSuccess) {
        throw new Error(result.message || 'Failed to update user role');
      }
      return result.value;
    },
    onSuccess: (_, variables) => {
      // Invalidate the user query to refetch updated data
      queryClient.invalidateQueries({
        queryKey: identityKeys.user(variables.userId),
      });
    },
  });
}

// =============================================================================
// TENANT MEMBER HOOKS
// =============================================================================

/**
 * Hook to get members of a tenant
 */
export function useTenantMembers(tenantId: string) {
  return useQuery({
    queryKey: identityKeys.members(tenantId),
    queryFn: async () => {
      const result = await getTenantMembers(tenantId);
      if (!result.isSuccess) {
        throw new Error(result.message || 'Failed to get tenant members');
      }
      return result.value as TenantMember[];
    },
    enabled: !!tenantId,
  });
}

/**
 * Hook to remove a member from a tenant
 */
export function useRemoveTenantMember() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (request: RemoveMemberRequest) => {
      const result = await removeTenantMember(request);
      if (!result.isSuccess) {
        throw new Error(result.message || 'Failed to remove member');
      }
      return result.value;
    },
    onSuccess: (_, variables) => {
      // Invalidate the members query to refetch
      queryClient.invalidateQueries({
        queryKey: identityKeys.members(variables.tenantId),
      });
    },
  });
}

// =============================================================================
// INVITATION HOOKS
// =============================================================================

/**
 * Hook to get invitations for a tenant
 */
export function useInvitations(tenantId: string) {
  return useQuery({
    queryKey: identityKeys.invitations(tenantId),
    queryFn: async () => {
      const result = await getTenantInvitations(tenantId);
      if (!result.isSuccess) {
        throw new Error(result.message || 'Failed to get invitations');
      }
      return result.value as Invitation[];
    },
    enabled: !!tenantId,
  });
}

/**
 * Hook to create an invitation
 */
export function useCreateInvitation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (request: CreateInvitationRequest) => {
      const result = await createInvitation(request);
      if (!result.isSuccess) {
        throw new Error(result.message || 'Failed to create invitation');
      }
      return result.value as Invitation;
    },
    onSuccess: (_, variables) => {
      // Invalidate invitations query to refetch
      queryClient.invalidateQueries({
        queryKey: identityKeys.invitations(variables.tenantId),
      });
    },
  });
}

/**
 * Hook to accept an invitation
 */
export function useAcceptInvitation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (token: string) => {
      const result = await acceptInvitation(token);
      if (!result.isSuccess) {
        throw new Error(result.message || 'Failed to accept invitation');
      }
      return result.value;
    },
    onSuccess: () => {
      // Invalidate current user to refetch with new membership
      queryClient.invalidateQueries({
        queryKey: identityKeys.currentUser(),
      });
    },
  });
}

/**
 * Hook to revoke an invitation
 */
export function useRevokeInvitation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      tenantId,
      invitationId,
    }: {
      tenantId: string;
      invitationId: string;
    }) => {
      const result = await revokeInvitation(tenantId, invitationId);
      if (!result.isSuccess) {
        throw new Error(result.message || 'Failed to revoke invitation');
      }
      return result.value;
    },
    onSuccess: (_, variables) => {
      // Invalidate invitations query to refetch
      queryClient.invalidateQueries({
        queryKey: identityKeys.invitations(variables.tenantId),
      });
    },
  });
}

/**
 * Hook to resend an invitation
 */
export function useResendInvitation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      tenantId,
      invitationId,
    }: {
      tenantId: string;
      invitationId: string;
    }) => {
      const result = await resendInvitation(tenantId, invitationId);
      if (!result.isSuccess) {
        throw new Error(result.message || 'Failed to resend invitation');
      }
      return result.value as Invitation;
    },
    onSuccess: (_, variables) => {
      // Invalidate invitations query to refetch
      queryClient.invalidateQueries({
        queryKey: identityKeys.invitations(variables.tenantId),
      });
    },
  });
}
